import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import type { MetricRange, MetricsResponse } from './metrics'

export interface GpuPoint {
  b: string
  util_pct: number | null
  mem_used: number | null
  mem_used_max: number | null
  mem_total: number | null
  temp_c: number | null
  power_w: number | null
}

export interface GpuProcess {
  pid: number
  process_name: string | null
  container_id: string | null
  avg_used_mem: number | null
  max_used_mem: number | null
  last_seen: string
}

export interface GpuProcessesResponse {
  range: MetricRange
  gpu_uuid: string | null
  processes: GpuProcess[]
}

export interface GpuClusterSummary {
  total_gpus: number
  online_gpus: number
  active_gpus: number
  avg_util_pct: number | null
  total_mem_used: number
  total_mem_capacity: number
  hottest_temp_c: number | null
}

export function useGpuMetrics(
  nodeId: string | undefined,
  gpuIndex: number,
  range: MetricRange,
) {
  return useQuery<MetricsResponse<GpuPoint>>({
    queryKey: ['metrics', 'gpus', nodeId, gpuIndex, range],
    queryFn: () =>
      api<MetricsResponse<GpuPoint>>(
        `/api/metrics/gpus/${nodeId}/${gpuIndex}?range=${range}`,
      ),
    enabled: !!nodeId,
    refetchInterval: range === '1h' ? 15_000 : 60_000,
  })
}

export function useGpuProcesses(
  nodeId: string | undefined,
  gpuIndex: number,
  range: MetricRange,
) {
  return useQuery<GpuProcessesResponse>({
    queryKey: ['metrics', 'gpus', 'processes', nodeId, gpuIndex, range],
    queryFn: () =>
      api<GpuProcessesResponse>(
        `/api/metrics/gpus/${nodeId}/${gpuIndex}/processes?range=${range}`,
      ),
    enabled: !!nodeId,
    refetchInterval: 30_000,
  })
}

export function useGpuClusterSummary() {
  return useQuery<GpuClusterSummary>({
    queryKey: ['cluster', 'gpus', 'summary'],
    queryFn: () => api<GpuClusterSummary>('/api/cluster/gpus/summary'),
    refetchInterval: 30_000,
  })
}
